import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import { useLessons } from "./useLessons";
import { parseDateId, addDays, formatDateId } from "../utils/date";

export interface WeekCompletionInfo {
  videoTotal: number;
  videoWatched: number;
  pct: number;
}

export type WeekGateState = "completed" | "active" | "locked" | "unlockable";

export interface WeekUnlockResult {
  unlockedWeek: number;
  pendingUnlockWeek: number | null;
  weekStates: Record<number, WeekGateState>;
  weekCompletions: Record<number, WeekCompletionInfo>;
  showPopupForWeek: number | null;
  triggerPopup: () => void;
  dismissPopup: () => void;
  confirmUnlock: () => Promise<void>;
  loading: boolean;
  startDateId: string | null;
}

export interface RecoveryProgramme {
  startDateId: string;
  totalWeeks: number;
}

const TOTAL_WEEKS = 6;
const WEEK_UNLOCK_THRESHOLD = 80;
const LESSON_WATCHED_PCT = 80;

function isTimeMet(weekNumber: number, startDateId: string): boolean {
  const start = parseDateId(startDateId);
  if (!start) return false;
  const weekStart = addDays(start, (weekNumber - 1) * 7);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today >= weekStart;
}

export function useAllWeeksUnlock(): WeekUnlockResult {
  const { user } = useAuth();

  const { weeks: filteredWeeks, progress: progressMap, loading: lessonsLoading } = useLessons();

  const [programme, setProgramme] = useState<RecoveryProgramme | null>(null);
  const [confirmedWeek, setConfirmedWeek] = useState<number>(1);
  const [loadingProgramme, setLoadingProgramme] = useState(true);

  const [showPopupForWeek, setShowPopupForWeek] = useState<number | null>(null);
  const autoShownRef = useRef<number | null>(null); // ← changed from useRef(false)

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.uid) {
        setLoadingProgramme(false);
        return;
      }

      setLoadingProgramme(true);
      try {
        const progRef = doc(db, "users", user.uid, "recoveryProgrammes", "physical_wellbeing_6w");
        const progSnap = await getDoc(progRef);

        if (cancelled) return;

        if (progSnap.exists()) {
          const d = progSnap.data();
          setProgramme({
            startDateId: d.startDateId,
            totalWeeks: d.totalWeeks ?? 6,
          });
          setConfirmedWeek(typeof d.confirmedWeek === "number" ? d.confirmedWeek : 1);
        } else {
          const userSnap = await getDoc(doc(db, "users", user.uid));
          if (cancelled) return;

          let fallbackDateId = formatDateId(new Date());

          if (userSnap.exists()) {
            const userData = userSnap.data();
            if (userData.createdAt instanceof Timestamp) {
              fallbackDateId = formatDateId(userData.createdAt.toDate());
            } else if (typeof userData.createdAt === "string") {
              fallbackDateId = userData.createdAt;
            }
          }

          setProgramme({ startDateId: fallbackDateId, totalWeeks: 6 });

          await setDoc(progRef, {
            programmeId: "physical_wellbeing_6w",
            startDateId: fallbackDateId,
            totalWeeks: 6,
            status: "active",
            confirmedWeek: 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
      } catch (err) {
        const error = err as { code?: string };
        if (error.code === 'permission-denied') {
          console.warn("[useAllWeeksUnlock] Initializing... waiting for permissions.");
        } else {
          console.error("[useAllWeeksUnlock] fetch error:", err);
        }
      } finally {
        if (!cancelled) setLoadingProgramme(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const weekCompletions = useMemo(() => {
    const map: Record<number, WeekCompletionInfo> = {};
    for (let w = 1; w <= TOTAL_WEEKS; w++) {
      const lessons = (filteredWeeks[w] ?? []).filter(l => !!l.vimeoId);
      const total = lessons.length;

      if (total === 0) {
        map[w] = { videoTotal: 0, videoWatched: 0, pct: 100 };
        continue;
      }

      const watched = lessons.filter((l) => {
        const p = progressMap[l.id];
        return p && (p.status === "completed" || p.percentage >= LESSON_WATCHED_PCT);
      }).length;

      map[w] = {
        videoTotal: total,
        videoWatched: watched,
        pct: Math.round((watched / total) * 100),
      };
    }
    return map;
  }, [filteredWeeks, progressMap]);

  const maxEligibleWeek = useMemo(() => {
    let max = 1;
    for (let w = 2; w <= TOTAL_WEEKS; w++) {
      const priorDone = (weekCompletions[w - 1]?.pct ?? 0) >= WEEK_UNLOCK_THRESHOLD;
      const timePassed = programme?.startDateId ? isTimeMet(w, programme.startDateId) : false;

      if (priorDone && timePassed) {
        max = w;
      } else {
        break;
      }
    }
    return max;
  }, [weekCompletions, programme]);

  const pendingUnlockWeek = confirmedWeek < maxEligibleWeek ? confirmedWeek + 1 : null;

  const weekStates = useMemo(() => {
    const states: Record<number, WeekGateState> = {};
    for (let w = 1; w <= TOTAL_WEEKS; w++) {
      if (w < confirmedWeek) states[w] = "completed";
      else if (w === confirmedWeek) states[w] = "active";
      else if (w === pendingUnlockWeek) states[w] = "unlockable";
      else states[w] = "locked";
    }
    return states;
  }, [confirmedWeek, pendingUnlockWeek]);

  const loading = loadingProgramme || lessonsLoading;

  // ← replaced auto-show effect
  useEffect(() => {
    if (loading) return;
    if (!pendingUnlockWeek) return;
    if (autoShownRef.current === pendingUnlockWeek) return;
    autoShownRef.current = pendingUnlockWeek;
    setShowPopupForWeek(pendingUnlockWeek);
  }, [loading, pendingUnlockWeek]);

  const triggerPopup = useCallback(() => {
    if (pendingUnlockWeek) setShowPopupForWeek(pendingUnlockWeek);
  }, [pendingUnlockWeek]);

  const dismissPopup = useCallback(() => setShowPopupForWeek(null), []);

  const confirmUnlock = useCallback(async () => {
    if (!pendingUnlockWeek || !user) return;
    const newConfirmed = pendingUnlockWeek;
    setConfirmedWeek(newConfirmed);
    setShowPopupForWeek(null);
    autoShownRef.current = newConfirmed; // ← reset to confirmed week, not null

    try {
      const progRef = doc(db, "users", user.uid, "recoveryProgrammes", "physical_wellbeing_6w");
      await setDoc(progRef, { confirmedWeek: newConfirmed, updatedAt: serverTimestamp() }, { merge: true });
    } catch (err) {
      console.error("[useAllWeeksUnlock] Failed to persist:", err);
    }
  }, [pendingUnlockWeek, user]);

  return {
    unlockedWeek: confirmedWeek,
    pendingUnlockWeek,
    weekStates,
    weekCompletions,
    showPopupForWeek,
    triggerPopup,
    dismissPopup,
    confirmUnlock,
    loading,
    startDateId: programme?.startDateId ?? null,
  };
}